import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { CheckCircle } from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';
import ReactMarkdown from 'react-markdown';

export default function SignLetter() {
  const { assignmentId } = useParams();
  const navigate = useNavigate();
  const [assignment, setAssignment] = useState(null);
  const [loading, setLoading] = useState(true);
  const [signing, setSigning] = useState(false);
  const [signed, setSigned] = useState(false);

  useEffect(() => {
    loadLetter();
  }, [assignmentId]);

  const loadLetter = async () => {
    try {
      const response = await api.get('/api/engagement-letters-legacy/assignments');
      const letter = response.data.find(a => a.id === assignmentId);
      
      if (!letter) {
        toast.error('Engagement letter not found');
        navigate('/dashboard');
        return;
      }
      
      setAssignment(letter);
      setSigned(letter.status === 'signed');
    } catch (error) {
      toast.error('Failed to load engagement letter');
      navigate('/dashboard');
    } finally {
      setLoading(false);
    }
  };

  const handleSign = async () => {
    if (!confirm('By clicking OK, you agree to the terms in this engagement letter.')) {
      return;
    }

    setSigning(true);
    try {
      await api.post(`/api/engagement-letters-legacy/${assignmentId}/sign`);
      toast.success('Engagement letter signed successfully!');
      setSigned(true);
      loadLetter();
    } catch (error) {
      toast.error('Failed to sign letter');
    } finally {
      setSigning(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-muted/50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (signed) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-background to-muted px-4">
        <Card className="w-full max-w-2xl shadow-lg">
          <CardHeader className="text-center">
            <div className="flex justify-center mb-4">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center">
                <CheckCircle className="w-10 h-10 text-green-600" />
              </div>
            </div>
            <CardTitle className="text-2xl font-bold text-foreground" style={{fontFamily: 'Plus Jakarta Sans, sans-serif'}}>
              Engagement Letter Signed
            </CardTitle>
          </CardHeader>
          <CardContent className="text-center">
            <p className="text-muted-foreground">Thank you for signing the engagement letter. Your engagement is now active.</p>
            <Button onClick={() => navigate('/dashboard')} className="mt-4">Back to Dashboard</Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-background to-muted py-12 px-4">
      <div className="max-w-4xl mx-auto">
        <Card className="shadow-lg">
          <CardHeader>
            <CardTitle className="text-2xl" style={{fontFamily: 'Plus Jakarta Sans, sans-serif'}}>
              Engagement Letter for Review
            </CardTitle>
            <p className="text-sm text-muted-foreground mt-2">Please review the engagement letter below and sign to proceed</p>
          </CardHeader>
          <CardContent>
            <div className="prose prose-slate max-w-none p-6 bg-card border-2 border-border rounded-lg mb-6">
              <ReactMarkdown>{assignment?.rendered_content}</ReactMarkdown>
            </div>

            <div className="p-4 bg-blue-50 rounded-lg border border-blue-200 mb-6">
              <p className="text-sm text-blue-800">
                By signing this engagement letter, you agree to the terms and conditions outlined above.
                Your signature will be recorded with timestamp and IP address for audit purposes.
              </p>
            </div>

            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => navigate('/dashboard')}>Cancel</Button>
              <Button onClick={handleSign} disabled={signing} className="bg-primary hover:bg-primary/90">
                {signing ? 'Signing...' : 'Sign Engagement Letter'}
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
